/** @jsx jsx */

import { css, jsx } from '@emotion/react';
import { labelStyles } from '@zapier/style-encapsulation';
import { Accordion } from '../../display/Accordion';
import { Heading, HeadingLevel } from '../../display/Heading';
import { Button } from '../../navigation/Button';

type FAQItem = {
  title: string;
  content: React.ReactNode;
  href?: string;
  isInitiallyOpen?: boolean;
  isOpen?: boolean;
  onToggle?: ({ isOpen }: { isOpen: boolean }) => void;
};

export type Props = {
  /** An array of FAQ items (accordions) to render. */
  items?: FAQItem[];
  /** Optional content to render in place of `items`. */
  children?: React.ReactNode;
  /** Optional override for section heading. */
  heading?: string;
  /** Text for optional call-to-action button. */
  ctaText?: string;
  /** URL for optional call-to-action button. */
  ctaHref?: string;
  /** Click handler for optional call-to-action button. */
  onCtaClick?: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
};

const Styles = labelStyles('FAQ', {
  root: css`
    padding: var(--zds-space-32) var(--zds-space-20);

    @media screen and (min-width: 650px) {
      padding: var(--zds-space-48) var(--zds-space-32);
    }
  `,
  faqContent: css`
    max-width: 1170px;
    margin: 0 auto;
  `,
  heading: css`
    margin-bottom: var(--zds-space-32);
    font: var(--zds-section-header);
    color: var(--zds-text-stronger);
  `,
  ctaContainer: css`
    margin-top: var(--zds-space-32);

    & > a,
    & > button {
      width: 100%;
    }

    @media screen and (min-width: 650px) {
      margin-top: var(--zds-space-32);
      & > a,
      & > button {
        width: auto;
      }
    }
  `,
  grid: css`
    display: grid;
    gap: var(--zds-space-20);

    @media screen and (min-width: 650px) {
      gap: var(--zds-space-16);
    }
  `,
});

/**
 * `FAQ` is used to group frequently asked questions ([Accordion](../?path=/docs/display-accordion--docs)'s) with a heading.
 */
export const FAQ = ({
  heading = 'Frequently Asked Questions',
  items,
  children,
  ctaText,
  ctaHref,
  onCtaClick,
}: Props) => {
  return (
    <div css={Styles.root} data-zds>
      <div css={Styles.faqContent} data-zds>
        <HeadingLevel>
          <Heading css={Styles.heading}>{heading}</Heading>
        </HeadingLevel>
        {items && items.length > 0 ? (
          <div css={Styles.grid} data-zds>
            {items.map((item) => (
              <Accordion
                key={item.title}
                title={item.title}
                href={item.href}
                isInitiallyOpen={item.isInitiallyOpen}
                isOpen={item.isOpen}
                onToggle={item.onToggle}
              >
                {item.content}
              </Accordion>
            ))}
          </div>
        ) : (
          children
        )}
        {ctaText && (ctaHref || onCtaClick) && (
          <div css={Styles.ctaContainer} data-zds>
            <Button href={ctaHref} size="small" onClick={onCtaClick}>
              {ctaText}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
